import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { useLocation, useHistory } from "react-router-dom";
import * as folderActions from '../actionCreator';
import * as dictionariesActions from 'actions/actionCreators/dictionariesActions';
import * as clientActions from 'features/offer/edit/client/form/actionCreators';
import FolderList from './FolderList';
import FolderListTopToolbar from './FolderListTopToolbar';
import { useTranslation } from 'react-i18next';
import { Grid } from '@mui/material';
import { objToQueryString, clean, getInt, getDateValue } from 'utils/functions';
import GridOn from '@mui/icons-material/GridOn';
import HeaderPortal from '../../../../components/layout/HeaderPortal';
import useDebounce from 'utils/hooks/useDebounce';
import documentTypeIds from 'constants/documentTypeIds';
import { showInfo } from 'actions/actionCreators/snackbarActions';
import * as visibilityLevels from 'constants/visibilityLevels';
import { Card } from '@totalsoft/rocket-ui';
import PaginationWrapper from 'components/common/PaginationWrapper';
import { FoldersListWrapper } from './FolderListStyles';
import WrappedFakeText from 'components/common/WrappedFakeText';
import { emptyString } from 'constants/emptyConst';

const DEBOUNCE_DELAY = 500;

const blankFilters = {
  clientName: null,
  clientCnpOrCui: null,
  fullTextFilter: '',
  folderNumber: null,
  offerNumbers: null,
  channelTypeId: null,
  stateIds: [],
  folderStartDate: null,
  folderEndDate: null,
  amountFrom: null,
  amountTo: null,
  currencyId: null,
  responsibleId: null,
  dealerId: null,
  userPartnerId: null,
  visibilityLevelId: null,
  offerTypeId: null,
  partnerTypeId: null,
  riskAnalysisTypeId: null,
  relationTypeId: null,
  orderByColumnName: 'InternalFolderDate',
  orderByDescending: true
};

const defaultPager = { page: 1, pageSize: 10, totalCount: 0 };

const FolderListContainer = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();

  const userInfo = useSelector(state => state.userInfo, shallowEqual);
  const userPreferences = useSelector(state => state.folderVisible.userPreferences, shallowEqual);
  const selectedUserPreference = useSelector(state => state.folderVisible.selectedUserPreference, shallowEqual);
  const defaultUserPreference = useSelector(state => state.folderVisible.defaultUserPreference, shallowEqual);

  const isSearchingRestrictedResponsible = userInfo.isLoaded && userInfo.userExtraInfoModel.isSearchingRestrictedResponsible;
  const isSearchingRestrictedDealer = userInfo.isLoaded && userInfo.userExtraInfoModel.isSearchingRestrictedDealer;
  const isSearchingRestrictedUserPartner = userInfo.isLoaded && userInfo.userExtraInfoModel.isSearchingRestrictedUserPartner;

  const [folders, setFolders] = useState(null);
  const [filters, setFilters] = useState(null);
  const [pager, setPager] = useState(defaultPager);
  const [currentSelectedUserPreference, setCurrentSelectedUserPreference] = useState(null);

  const visibleFilters = currentSelectedUserPreference?.filters || selectedUserPreference.filters;
  const visibleFields = currentSelectedUserPreference?.fields || selectedUserPreference.fields;
  const fieldsToShow = visibleFields.filter(x => x.isVisible).map(x => x.fieldName);

  const debounceFiltersValueChanged = useDebounce(filters, DEBOUNCE_DELAY);

  const initCurrentPreference = newPreference => initFilters({ ...getFilterValuesFromUserPreference(newPreference), fullTextFilter: '' });

  const getFiltersFromURL = value => {
    const url = new URLSearchParams(value);
    return {
      clientName: url.get('clientName'),
      clientCnpOrCui: url.get('clientCnpOrCui'),
      fullTextFilter: url.get('fullTextFilter'),
      folderNumber: url.get('folderNumber'),
      offerNumbers: url.get('offerNumbers'),
      channelTypeId: getInt(url.get('channelTypeId')),
      stateIds: url.getAll('stateIds').flatMap(v => v.split(',')).map(getInt).filter(id => id !== null),
      folderStartDate: url.get('folderStartDate'),
      folderEndDate: url.get('folderEndDate'),
      amountFrom: url.get('amountFrom'),
      amountTo: url.get('amountTo'),
      currencyId: getInt(url.get('currencyId')),
      responsibleId: getInt(url.get('responsibleId')),
      dealerId: getInt(url.get('dealerId')),
      userPartnerId: getInt(url.get('userPartnerId')),
      visibilityLevelId: getInt(url.get('visibilityLevelId')),
      offerTypeId: getInt(url.get('folderTypeId')) ?? getInt(url.get('offerTypeId')),
      partnerTypeId: getInt(url.get('partnerTypeId')),
      riskAnalysisTypeId: getInt(url.get('riskAnalysisTypeId')),
      relationTypeId: getInt(url.get('relationTypeId')),
      orderByDescending: JSON.parse(url.get('orderByDescending')),
      orderByColumnName: url.get('orderByColumnName')
    };
  };

  const getPagerFromURL = value => {
    const url = new URLSearchParams(value);
    return {
      page: getInt(url.get('page')) ?? defaultPager.page,
      pageSize: getInt(url.get('pageSize')) ?? defaultPager.pageSize,
      totalCount: defaultPager.totalCount
    };
  };

  const getFilterValuesFromUserPreference = useCallback((userPreference) => {
    let newFilters = { ...blankFilters };
    if (!userPreference) return newFilters;

    userPreference.filters.forEach(el => { newFilters[el.fieldName] = el.defaultValue; });
    if (userPreference.order) {
      newFilters.orderByColumnName = userPreference.order.orderByColumnName;
      newFilters.orderByDescending = userPreference.order.orderByDescending;
    }

    return getFiltersFromURL(objToQueryString(newFilters));
  }, []);

  const getQueryStringFromFiltersAndPaging = (filters, pager) =>
    objToQueryString({
      ...filters,
      page: pager.page === defaultPager.page ? '' : pager.page,
      pageSize: pager.pageSize === defaultPager.pageSize ? '' : pager.pageSize
    }).replace('offerTypeId', 'folderTypeId');

  const filterFolders = useCallback((searchFilters, searchPager) => {
    async function loadFoldersAsync() {
      const loadedFolders = await dispatch(folderActions.loadFolders(getQueryStringFromFiltersAndPaging(searchFilters, searchPager)));
      setFolders(loadedFolders);
    }

    loadFoldersAsync();
  }, [dispatch]);

  const pushFiltersAndPagingToQueryString = useCallback((newFilters, newPager) => {
    if (!userPreferences.isLoaded) return;
    if (newFilters?.folderStartDate) newFilters.folderStartDate = getDateValue(newFilters.folderStartDate);
    if (newFilters?.folderEndDate) newFilters.folderEndDate = getDateValue(newFilters.folderEndDate);
    if (newFilters?.folderStartDate !== emptyString && newFilters?.folderEndDate !== emptyString) {
      history.push('/folders?' + getQueryStringFromFiltersAndPaging(newFilters, newPager));
      filterFolders(newFilters, newPager);
    }
  }, [userPreferences.isLoaded, history, filterFolders]);

  const initFilters = useCallback(async (receivedFilters) => {
    let newFilters = { ...receivedFilters };

    if (newFilters.visibilityLevelId === visibilityLevels.MyDocuments)
      newFilters.responsibleId = userInfo.userExtraInfoModel.personId;

    if (newFilters.responsibleId !== null && (!newFilters?.responsible || newFilters.responsibleId !== newFilters?.responsible?.responsibleId))
      newFilters.responsible = await dispatch(dictionariesActions.loadResponsible(newFilters.responsibleId));

    if (newFilters.dealerId !== null && (!newFilters?.dealer || newFilters.dealerId !== newFilters?.dealer?.dealerId))
      newFilters.dealer = await dispatch(dictionariesActions.loadDealer(newFilters.dealerId));

    if (newFilters.userPartnerId !== null && (!newFilters?.userPartner || newFilters.userPartnerId !== newFilters?.userPartner?.userPartnerId))
      newFilters.userPartner = await dispatch(dictionariesActions.loadUserPartner(newFilters.userPartnerId));

    if (newFilters.visibilityLevelId !== null && (!newFilters?.visibilityLevel || newFilters.visibilityLevelId !== newFilters?.visibilityLevel?.visibilityLevelId))
      newFilters.visibilityLevel = await dispatch(dictionariesActions.loadFolderVisibilityLevels()).then(data => { return data?.find(d => d.visibilityLevelId === newFilters.visibilityLevelId); });

    if (newFilters.channelTypeId !== null && (!newFilters?.channelType || newFilters.channelTypeId !== newFilters?.channelType?.channelTypeId))
      newFilters.channelType = await dispatch(dictionariesActions.loadChannels()).then(data => { return data?.find(d => d.channelTypeId === newFilters.channelTypeId); });

    if (newFilters.partnerTypeId !== null && (!newFilters?.partnerType || newFilters.partnerTypeId !== newFilters?.partnerType?.partnerTypeId))
      newFilters.partnerType = await dispatch(clientActions.loadPartnerTypes()).then(data => { return data.find(d => d.partnerTypeId === newFilters.partnerTypeId); });

    if (newFilters.stateIds?.length > 0)
      newFilters.states = await dispatch(dictionariesActions.loadStates(documentTypeIds.Folder)).then(data => { return data.filter(d => newFilters.stateIds.includes(d.stateId)); });
    else
      newFilters.states = [];

    if (newFilters.riskAnalysisTypeId !== null && (!newFilters?.riskAnalysisType || newFilters.riskAnalysisTypeId !== newFilters?.riskAnalysisType?.riskAnalysisTypeId))
      newFilters.riskAnalysisType = await dispatch(dictionariesActions.loadRiskAnalysisTypes(documentTypeIds.Folder)).then(data => { return data.find(d => d.dictionaryItemId === newFilters.riskAnalysisTypeId); });

    if (newFilters.currencyId !== null && (!newFilters?.currency || newFilters.currencyId !== newFilters?.currency?.currencyId))
      newFilters.currency = await dispatch(dictionariesActions.loadCurrencies()).then(data => { return data.find(d => d.currencyId === newFilters.currencyId); });

    if (newFilters.relationTypeId !== null && (!newFilters?.relationType || newFilters.relationTypeId !== newFilters?.relationType?.relationTypeId))
      newFilters.relationType = await dispatch(clientActions.loadRelations()).then(data => { return data.find(d => d.id === newFilters.relationTypeId); });

    if (newFilters.offerTypeId !== null && (!newFilters?.offerType || newFilters.offerTypeId !== newFilters?.offerType?.offerTypeId))
      newFilters.offerType = await dispatch(dictionariesActions.loadOfferTypes(documentTypeIds.Folder))
        .then(folderTypes => {
          const facilityApprovalTypes = dispatch(dictionariesActions.loadOfferTypes(documentTypeIds.FacilityApproval));
          return Promise.all([facilityApprovalTypes, folderTypes]);
        })
        .then(([facilityApprovalTypes, folderTypes]) => {
          return folderTypes.concat(facilityApprovalTypes).find(type => type.offerTypeId === newFilters.offerTypeId);
        });

    setFilters({ ...newFilters });
  }, [dispatch, userInfo.userExtraInfoModel.personId]);

  const initUserPreference = useCallback(() => {
    const matchedUserPreferences = userPreferences.filter(function (up) {
      const userPrefenceStringFilters = getFilterValuesFromUserPreference(up);
      const queryStringFilters = getFiltersFromURL(objToQueryString(debounceFiltersValueChanged));
      clean(userPrefenceStringFilters);
      clean(queryStringFilters);

      if (Object.keys(userPrefenceStringFilters).length !== Object.keys(queryStringFilters).length)
        return false;

      let matchedFilters = 0;
      Object.keys(userPrefenceStringFilters).forEach(function (key) {
        const preferenceValue = userPrefenceStringFilters[key];
        const queryValue = queryStringFilters[key];
        const isEqual = Array.isArray(preferenceValue) || Array.isArray(queryValue)
          ? String(preferenceValue) === String(queryValue)
          : preferenceValue === queryValue;
        if (isEqual) matchedFilters++;
      });

      return matchedFilters === Object.keys(queryStringFilters).length;
    });

    if (!!matchedUserPreferences && matchedUserPreferences.length > 0)
      setCurrentSelectedUserPreference(userPreferences.find(up => up.filterName === matchedUserPreferences[0].filterName));
    else
      setCurrentSelectedUserPreference(prevState => { return { ...(prevState ?? selectedUserPreference), filterName: '' } });
  }, [debounceFiltersValueChanged, selectedUserPreference, userPreferences, getFilterValuesFromUserPreference]);

  useEffect(() => {
    dispatch(folderActions.loadFolderUserPreferences());
  }, [dispatch]);

  useEffect(() => {
    if (!userPreferences.isLoaded || filters != null) return;
    let newFilters;
    if (location.search !== '') {
      setPager(getPagerFromURL(location.search));
      newFilters = getFiltersFromURL(location.search);
    }
    else
      newFilters = { ...getFilterValuesFromUserPreference(userPreferences.find(up => up.implicit) ?? defaultUserPreference) };

    initFilters({ ...newFilters, fullTextFilter: newFilters.fullTextFilter ?? '' });
  }, [userPreferences, defaultUserPreference, filters, location.search, getFilterValuesFromUserPreference, initFilters, initUserPreference]);

  useEffect(() => {
    if (!debounceFiltersValueChanged) return;
    initUserPreference();
    pushFiltersAndPagingToQueryString(debounceFiltersValueChanged, pager);
  }, [debounceFiltersValueChanged, pager, pushFiltersAndPagingToQueryString, initUserPreference]);

  const onRefresh = () => filterFolders(filters, pager);
  const onEdit = folder => {
    sessionStorage.setItem("FolderURLFilters", location.search);
    return `/folders/${folder.documentId}/${folder.siteId}`;
  };

  const onDownload = async fileType => {
    const queryStringFilters = getFiltersFromURL();
    clean(queryStringFilters);
    const defaultFilters = getFilterValuesFromUserPreference(currentSelectedUserPreference);
    const filters = {
      ...blankFilters,
      ...defaultFilters,
      ...queryStringFilters,
      fileType
    };
    const fieldsToShow = currentSelectedUserPreference?.fields
      .filter(x => x.isVisible === true)
      .map(x => {
        return { fieldName: x.fieldName, label: t('FolderList.' + x.label) };
      });

    const event = await dispatch(folderActions.exportFolders(filters, fieldsToShow));
    window.location = dispatch(folderActions.buildFolderExportDownloadUrl(event.filePath, event.fileName));
  };

  const onResetFilters = () => initFilters({ ...blankFilters });

  const onFilterValueChanged = async (name, value) => {
    let responsible = null;
    if (name === 'visibilityLevel') {
      if (value?.visibilityLevelId === visibilityLevels.MyDocuments)
        responsible = await dispatch(dictionariesActions.loadResponsible(userInfo.userExtraInfoModel.personId));

      setFilters(prev => {
        return {
          ...prev,
          [name]: value,
          visibilityLevelId: value?.visibilityLevelId,
          responsibleId: value?.visibilityLevelId === visibilityLevels.MyDocuments ? userInfo.userExtraInfoModel.personId : '',
          responsible
        }
      });
    }
    else
      setFilters(prevState => {
        return {
          ...prevState,
          [name]: value,
          responsibleId: name === 'responsible' ? value?.responsibleId : prevState.responsibleId,
          channelTypeId: name === 'channelType' ? value?.channelTypeId : prevState.channelTypeId,
          partnerTypeId: name === 'partnerType' ? value?.partnerTypeId : prevState.partnerTypeId,
          stateIds: name === 'states' ? (value || []).map(v => v.stateId) : prevState.stateIds,
          riskAnalysisTypeId: name === 'riskAnalysisType' ? value?.riskAnalysisTypeId : prevState.riskAnalysisTypeId,
          dealerId: name === 'dealer' ? value?.dealerId : prevState.dealerId,
          userPartnerId: name === 'userPartner' ? value?.partnerId : prevState.userPartnerId,
          currencyId: name === 'currency' ? value?.currencyId : prevState.currencyId,
          relationTypeId: name === 'relationType' ? value?.relationTypeId : prevState.relationTypeId,
          offerTypeId: name === 'offerType' ? value?.offerTypeId : prevState.offerTypeId
        }
      });
  };

  const onFilterVisibleValueChanged = (name, value) => {
    const filter = visibleFilters.find(x => x.fieldName === name);
    const index = visibleFilters.indexOf(filter);
    const newVisibleFilters = Object.assign([...visibleFilters], { [index]: { ...filter, isVisible: value } });
    setCurrentSelectedUserPreference({ ...currentSelectedUserPreference, ilterName: '', implicit: false, isDefault: false, filters: newVisibleFilters });
  };

  const onFieldsVisibleValueChanged = (name, value) => {
    const filter = visibleFields.find(x => x.fieldName === name);
    const index = visibleFields.indexOf(filter);
    const newVisibleFields = Object.assign([...visibleFields], { [index]: { ...filter, isVisible: value } });
    if (!newVisibleFields.some(x => x.isVisible === true)) return;

    setCurrentSelectedUserPreference({ ...currentSelectedUserPreference, ilterName: '', implicit: false, isDefault: false, fields: newVisibleFields });
  };

  const onChangePage = pageChanged => setPager(prev => { return { ...prev, page: pageChanged + 1 } });
  const onChangeRowsPerPage = pageSizeChanged => setPager(prev => { return { ...prev, pageSize: pageSizeChanged, page: 1 } });
  const onUserPreferenceChanged = name => initCurrentPreference(userPreferences.find(x => x.filterName === name));

  const onAddUserPreference = popUpUserPreference => {
    if (popUpUserPreference.filterName === '' || userPreferences.some(x => x.filterName.toLowerCase() === popUpUserPreference.filterName.toLowerCase())) return;

    const newPreference = {
      ...popUpUserPreference,
      isDefault: false,
      implicit: false,
      filterName: popUpUserPreference.filterName.trim(),
      filters: popUpUserPreference.filters.map(f => { return { ...f, defaultValue: filters[f.fieldName]?.toString() ?? '' } })
    };

    dispatch(folderActions.updateFolderUserPreferences([...userPreferences, newPreference]));
  };

  const onDeleteUserPreference = () => {
    if (currentSelectedUserPreference.isDefault) {
      dispatch(showInfo(t('OfferList.CantDeleteDefaultFilter')));
      return;
    }

    dispatch(folderActions.updateFolderUserPreferences(userPreferences.filter(p => p.filterName !== currentSelectedUserPreference.filterName)));
    const newPreference = {
      ...currentSelectedUserPreference,
      isDefault: false,
      implicit: false,
      filterName: '',
      filters: currentSelectedUserPreference.filters.map(f => { return { ...f, defaultValue: filters[f.fieldName]?.toString() ?? f.defaultValue } })
    };
    dispatch(folderActions.updateFolderSelectedUserPreference(newPreference));
    initCurrentPreference(newPreference);
  };

  const onListDeleteChanged = item => {
    if (item.isDefault) {
      dispatch(showInfo(t('OfferList.CantDeleteDefaultFilter')));
      return;
    }

    if (item.filterName === currentSelectedUserPreference.filterName)
      setCurrentSelectedUserPreference({ ...currentSelectedUserPreference, isDefault: false, filterName: '', implicit: false });

    dispatch(folderActions.updateFolderUserPreferences(userPreferences.filter(p => p.filterName !== item.filterName)));
  };

  const onListImplicitChanged = item => {
    const newUserPreferences = userPreferences.map(up => { return { ...up, implicit: up.filterName === item.filterName ? !item.implicit : false }; });
    dispatch(folderActions.updateFolderUserPreferences(newUserPreferences));
    const newPreference = newUserPreferences.find(p => p.implicit === true);
    if (newPreference) {
      dispatch(folderActions.updateFolderSelectedUserPreference(newPreference));
      initCurrentPreference(newPreference);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Card icon={GridOn}>
          <FoldersListWrapper>
            {debounceFiltersValueChanged &&
              <FolderListTopToolbar
                filters={filters || { ...blankFilters }}
                onFilterValueChanged={onFilterValueChanged}
                onFilterVisibleValueChanged={onFilterVisibleValueChanged}
                onFieldsVisibleValueChanged={onFieldsVisibleValueChanged}
                onResetFilters={onResetFilters}
                onDownload={onDownload}
                downloadEnabled={folders?.totalCount > 0}
                isSearchingRestrictedDealer={isSearchingRestrictedDealer}
                isSearchingRestrictedUserPartner={isSearchingRestrictedUserPartner}
                isSearchingRestrictedResponsible={isSearchingRestrictedResponsible}
                visibleFilters={[...visibleFilters]}
                visibleFields={[...visibleFields]}
                userPreferences={userPreferences}
                onUserPreferenceChanged={onUserPreferenceChanged}
                selectedUserPreference={currentSelectedUserPreference || selectedUserPreference}
                onAddUserPreference={onAddUserPreference}
                onDeleteUserPreference={onDeleteUserPreference}
                onListDeleteChanged={onListDeleteChanged}
                onListImplicitChanged={onListImplicitChanged}
              />
            }

            {!folders ? <WrappedFakeText /> : <FolderList folders={folders.values} fieldsToShow={fieldsToShow} onEdit={onEdit} />}

            <PaginationWrapper
              count={folders?.totalCount ?? defaultPager.totalCount}
              pageSize={folders?.pageSize ?? defaultPager.pageSize}
              page={folders?.page ?? defaultPager.page}
              onRowsPerPageChange={onChangeRowsPerPage}
              onPageChange={onChangePage}
              onRefresh={onRefresh}
            />
          </FoldersListWrapper>
        </Card>
      </Grid>
      <HeaderPortal />
    </Grid>
  );
};

export default FolderListContainer;
